import { QueryClient } from '@tanstack/react-query';

/**
 * Default query options for TanStack Query
 *
 * These settings control caching, retries, and refetching behavior
 * for all queries unless overridden at the individual query level.
 */
export const defaultQueryOptions = {
  queries: {
    // Stale time: 30 seconds - how long data is considered fresh
    staleTime: 30 * 1000,
    // Cache time: 5 minutes - how long inactive data stays in cache
    gcTime: 5 * 60 * 1000,
    // Retry failed requests up to 3 times
    retry: 3,
    // Retry delay with exponential backoff
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Refetch on window focus for fresh data
    refetchOnWindowFocus: true,
    // Refetch on reconnect for fresh data after network issues
    refetchOnReconnect: true,
  },
  mutations: {
    // Retry mutations once on failure
    retry: 1,
  },
} as const;

/**
 * Create a new QueryClient instance with default options
 *
 * Use this factory function to create a QueryClient instance.
 * This is useful for testing or creating multiple clients.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: defaultQueryOptions,
  });
}

/**
 * Singleton QueryClient instance for the application
 *
 * Import this directly for use with QueryClientProvider.
 * For testing, prefer using createQueryClient() to get a fresh instance.
 */
export const queryClient = createQueryClient();

/**
 * Query key factory for consistent key generation
 *
 * Use these factories to ensure consistent query key structure
 * across the application for invalidation and caching.
 */
export const queryKeys = {
  // Listings
  listings: {
    all: ['listings'] as const,
    lists: () => [...queryKeys.listings.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.listings.lists(), filters] as const,
    details: () => [...queryKeys.listings.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.listings.details(), id] as const,
    duplicates: (id: string) => [...queryKeys.listings.all, 'duplicates', id] as const,
    stats: () => [...queryKeys.listings.all, 'stats'] as const,
  },

  // Sources
  sources: {
    all: ['sources'] as const,
    lists: () => [...queryKeys.sources.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.sources.lists(), filters] as const,
    details: () => [...queryKeys.sources.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.sources.details(), id] as const,
    stats: () => [...queryKeys.sources.all, 'stats'] as const,
    scrapeRuns: (id: string) => [...queryKeys.sources.all, 'scrape-runs', id] as const,
  },

  // Areas
  areas: {
    all: ['areas'] as const,
    lists: () => [...queryKeys.areas.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.areas.lists(), filters] as const,
    details: () => [...queryKeys.areas.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.areas.details(), id] as const,
    bySlug: (slug: string) => [...queryKeys.areas.all, 'slug', slug] as const,
    stats: () => [...queryKeys.areas.all, 'stats'] as const,
  },

  // Rankings
  rankings: {
    all: ['rankings'] as const,
    lists: () => [...queryKeys.rankings.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.rankings.lists(), filters] as const,
    details: () => [...queryKeys.rankings.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.rankings.details(), id] as const,
    byListing: (listingId: string) => [...queryKeys.rankings.all, 'listing', listingId] as const,
    top: (filters: object) => [...queryKeys.rankings.all, 'top', filters] as const,
    config: () => [...queryKeys.rankings.all, 'config'] as const,
    stats: () => [...queryKeys.rankings.all, 'stats'] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    preferences: {
      all: () => [...queryKeys.notifications.all, 'preferences'] as const,
      lists: () => [...queryKeys.notifications.preferences.all(), 'list'] as const,
      list: (filters: object) => [...queryKeys.notifications.preferences.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.notifications.preferences.all(), 'detail', id] as const,
    },
    logs: {
      all: () => [...queryKeys.notifications.all, 'logs'] as const,
      recent: (filters: object) =>
        [...queryKeys.notifications.logs.all(), 'recent', filters] as const,
      stats: (filters: object) =>
        [...queryKeys.notifications.logs.all(), 'stats', filters] as const,
      byPreference: (preferenceId: string, filters: object) =>
        [...queryKeys.notifications.logs.all(), 'preference', preferenceId, filters] as const,
    },
    stats: () => [...queryKeys.notifications.all, 'stats'] as const,
  },

  // Saved Listings
  savedListings: {
    all: () => ['savedListings'] as const,
    lists: () => [...queryKeys.savedListings.all(), 'list'] as const,
    list: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.savedListings.lists(), filters] as const,
    check: (listingId: string) => [...queryKeys.savedListings.all(), 'check', listingId] as const,
    batchCheck: (listingIds: string[]) =>
      [...queryKeys.savedListings.all(), 'batchCheck', listingIds] as const,
  },

  // Health
  health: {
    all: ['health'] as const,
    status: () => [...queryKeys.health.all, 'status'] as const,
    ready: () => [...queryKeys.health.all, 'ready'] as const,
    live: () => [...queryKeys.health.all, 'live'] as const,
    db: () => [...queryKeys.health.all, 'db'] as const,
    redis: () => [...queryKeys.health.all, 'redis'] as const,
  },
} as const;

export default queryClient;
